import { useEffect, useState, type FormEvent } from "react";
import { useStartQuotation, useSuppliers, useWorkOrderSites } from "../../api/quotations";

interface Props {
  onStarted: (quotationId: number) => void;
}

/** Capture a quotation by hand, when there is no PDF to read: who is quoting,
 * their number and dates, and the work order it is for -- typed off the job
 * card, which finds the site in one step rather than a scroll through every
 * open job. The site joins its open job, or opens one under that number.
 *
 * It starts with no lines: the office keys them as the supplier quoted them,
 * and the service kit stays one click away under Standard service. */
export default function StartQuotation({ onStarted }: Props) {
  const suppliers = useSuppliers();
  const start = useStartQuotation();
  const [supplierId, setSupplierId] = useState("");
  const [number, setNumber] = useState("");
  const [quoteDate, setQuoteDate] = useState("");
  const [validUntil, setValidUntil] = useState("");
  const [workOrder, setWorkOrder] = useState("");
  const [lookedUp, setLookedUp] = useState("");
  const [siteId, setSiteId] = useState<number | null>(null);
  const found = useWorkOrderSites(lookedUp);
  const sites = found.data?.sites ?? [];

  // Looked up once the typing pauses, as the site search does.
  useEffect(() => {
    const timer = window.setTimeout(() => setLookedUp(workOrder.replace(/\s+/g, "")), 300);
    return () => window.clearTimeout(timer);
  }, [workOrder]);

  // One site under the number is the answer; several are a choice.
  useEffect(() => {
    setSiteId(sites.length === 1 ? sites[0].id : null);
  }, [found.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const ready = supplierId !== "" && number.trim() !== "" && siteId !== null;

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!ready) {
      return;
    }
    start.mutate(
      {
        inspection_id: null,
        site_id: siteId,
        supplier_id: Number(supplierId),
        number: number.trim(),
        quote_date: quoteDate || null,
        valid_until: validUntil || null,
        prefill: false,
      },
      { onSuccess: (detail) => onStarted(detail.id) },
    );
  }

  return (
    <form className="header-form cm-form start-form" aria-label="Capture a quotation by hand" onSubmit={onSubmit}>
      <h3>Capture a quotation by hand</h3>
      <label>
        Supplier
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} required>
          <option value="">Choose…</option>
          {(suppliers.data?.rows ?? []).map((row) => (
            <option key={row.id} value={row.id}>
              {row.name} ({row.acc_no})
            </option>
          ))}
        </select>
      </label>
      <label>
        Their quotation number
        <input value={number} onChange={(e) => setNumber(e.target.value)} placeholder="As printed on it" required />
      </label>
      <label>
        Quoted on
        <input type="date" value={quoteDate} onChange={(e) => setQuoteDate(e.target.value)} />
      </label>
      <label>
        Valid until
        <input type="date" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
      </label>
      <label>
        Work order number
        <input
          value={workOrder}
          onChange={(e) => setWorkOrder(e.target.value)}
          placeholder="From the job card, e.g. 511029052"
          inputMode="numeric"
          required
        />
      </label>
      <WorkOrderResult
        number={lookedUp}
        pending={found.isFetching}
        error={found.isError ? found.error.message : null}
        sites={sites}
        siteId={siteId}
        onChoose={setSiteId}
      />
      <button type="submit" className="btn btn-primary" disabled={start.isPending || !ready}>
        Start quotation
      </button>
      <p className="cm-doc-note">It starts with no lines: add them on the quotation as the supplier quoted them.</p>
      {start.isError ? <p className="notice bad">{start.error.message}</p> : null}
    </form>
  );
}

export function WorkOrderResult({
  number,
  pending,
  error,
  sites,
  siteId,
  onChoose,
}: {
  number: string;
  pending: boolean;
  error: string | null;
  sites: { id: number; name: string; cluster: string | null }[];
  siteId: number | null;
  onChoose: (id: number) => void;
}) {
  if (number === "") {
    return <p className="cm-doc-note">Type the work order number to find the site.</p>;
  }
  if (error !== null) {
    return (
      <p className="notice bad" role="alert">
        Could not look up the work order: {error}
      </p>
    );
  }
  if (pending) {
    return <p className="muted">Looking up {number}…</p>;
  }
  if (sites.length === 0) {
    return (
      <p className="notice">
        No site in the register has work order {number}. Check the number on the job card -- it is matched exactly.
      </p>
    );
  }
  return (
    <div className="qr-candidates" role="radiogroup" aria-label="The site this quotation is for">
      {sites.length > 1 ? <p className="cm-doc-note">{sites.length} sites share this number: choose the right one.</p> : null}
      {sites.map((site) => (
        <label key={site.id} className={site.id === siteId ? "qr-candidate on" : "qr-candidate"}>
          <input type="radio" name="wo-site" checked={site.id === siteId} onChange={() => onChoose(site.id)} />
          <span className="qr-candidate-text">
            <strong>{site.name}</strong>
            <span>
              Work order {number}
              {site.cluster ? ` · ${site.cluster}` : ""}
            </span>
          </span>
        </label>
      ))}
    </div>
  );
}
